import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Image, ProgressBar, Badge } from 'react-bootstrap';
import { FaUpload, FaTrash, FaCompress } from 'react-icons/fa';
import { prepareImageForUpload } from '../utils/imageCompression';

const LogoUploadModal = ({ 
  show, 
  onHide, 
  logoType, 
  currentLogoUrl, 
  onLogoUpload 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState(null);
  const [error, setError] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      
      // Créer un aperçu
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCompressAndUpload = async () => {
    if (!selectedFile) return;

    setIsCompressing(true);
    setError(null);

    try {
      const result = await prepareImageForUpload(selectedFile);
      const compressedFile = result.file;

      setCompressionInfo({
        originalSize: result.originalSize,
        compressedSize: result.finalSize,
        compressionRatio: Math.round((1 - result.finalSize / result.originalSize) * 100)
      });

      // Simuler l'upload (dans un vrai projet, vous feriez un appel API)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Créer une URL temporaire pour l'aperçu
      const logoUrl = URL.createObjectURL(compressedFile);
      
      onLogoUpload(logoType, logoUrl);
      handleClose();
      
    } catch (err) {
      console.error('Erreur lors de la compression:', err);
      setError('Erreur lors de la compression de l\'image: ' + err.message);
    } finally {
      setIsCompressing(false);
    }
  };

  const handleRemoveLogo = () => {
    onLogoUpload(logoType, null);
    handleClose();
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    setCompressionInfo(null);
    setError(null);
    onHide();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getLogoTypeLabel = () => {
    return logoType === 'ecoti' ? 'Logo ECOTI (vertical)' : 'Logo Secondaire (carré)';
  };

  const getRecommendedDimensions = () => {
    return logoType === 'ecoti' 
      ? 'Dimensions recommandées: 200x400px (format vertical)'
      : 'Dimensions recommandées: 200x200px (format carré)';
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUpload className="me-2" />
          Charger {getLogoTypeLabel()}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <div className="row">
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>
                Sélectionner un fichier image
                <Badge bg="info" className="ms-2">
                  {getRecommendedDimensions()}
                </Badge>
              </Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="mb-2"
              />
              <Form.Text className="text-muted">
                Formats acceptés: JPG, PNG, GIF. Taille maximale: 5MB
              </Form.Text>
            </Form.Group>

            {selectedFile && (
              <div className="mb-3">
                <h6>Informations du fichier:</h6>
                <ul className="list-unstyled">
                  <li><strong>Nom:</strong> {selectedFile.name}</li>
                  <li><strong>Taille:</strong> {formatFileSize(selectedFile.size)}</li>
                  <li><strong>Type:</strong> {selectedFile.type}</li>
                </ul>
              </div>
            )}

            {isCompressing && (
              <div className="mb-3">
                <h6>Compression en cours...</h6>
                <ProgressBar animated now={100} />
              </div>
            )}

            {compressionInfo && (
              <Alert variant="success" className="mb-3">
                <h6>Compression réussie!</h6>
                <ul className="mb-0">
                  <li>Taille originale: {formatFileSize(compressionInfo.originalSize)}</li>
                  <li>Taille compressée: {formatFileSize(compressionInfo.compressedSize)}</li>
                  <li>Réduction: {compressionInfo.compressionRatio}%</li>
                </ul>
              </Alert>
            )}
          </div>

          <div className="col-md-6">
            <h6>Aperçu:</h6>
            <div className="border rounded p-3 text-center" style={{ minHeight: '200px' }}>
              {preview ? (
                <div>
                  <Image 
                    src={preview} 
                    fluid 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '150px',
                      objectFit: 'contain'
                    }}
                  />
                  <div className="mt-2">
                    <Badge bg="secondary">
                      {logoType === 'ecoti' ? 'Format vertical' : 'Format carré'}
                    </Badge>
                  </div>
                </div>
              ) : currentLogoUrl ? (
                <div>
                  <Image 
                    src={currentLogoUrl} 
                    fluid 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '150px',
                      objectFit: 'contain'
                    }}
                  />
                  <div className="mt-2">
                    <Badge bg="info">Logo actuel</Badge>
                  </div>
                </div>
              ) : (
                <div className="text-muted">
                  <FaUpload size={48} className="mb-2" />
                  <div>Aucun logo sélectionné</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Annuler
        </Button>
        
        {currentLogoUrl && (
          <Button variant="danger" onClick={handleRemoveLogo}>
            <FaTrash className="me-1" />
            Supprimer le logo
          </Button>
        )}
        
        <Button 
          variant="primary" 
          onClick={handleCompressAndUpload}
          disabled={!selectedFile || isCompressing}
        >
          {isCompressing ? (
            <>
              <FaCompress className="me-1" />
              Compression...
            </>
          ) : (
            <>
              <FaUpload className="me-1" />
              Charger le logo
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LogoUploadModal;
